import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import dayjs from "dayjs";

import {
  createBonus,
  fetchUsers,
  handleError,
  rejectBonus,
  validateBonus,
  validateBonuses
} from "../../../modules/presence/api/api";
import { Button, Icon, Input, InputNumber, Modal, Table } from "antd";
import { BONUS_TYPE_ID, FREQUENCY_ID, roles, BONUS_STATUS_ID } from "../../../configs/constants";
import { get } from "../../../modules/presence/utils/utils";
import { isBonusFormValid } from "./Premium.utils";

import { SelectMonthBonus } from "./SelectMonth.component";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const START_DATE_INIT = moment()
  .subtract(1, "month")
  .startOf("month");
const END_DATE_INIT = moment()
  .subtract(1, "month")
  .endOf("month");

const BonusInput = ({
  bonus = {},
  onUpdate,
  onCreate,
  isExtra,
  action,
  hideEdit = false,
  roleId,
  onValidate,
  onReject
}) => {
  const status = bonus.status || {};
  const isValidated = status.id === BONUS_STATUS_ID.VALIDATED;
  const initialAmount = bonus.amount >= 0 ? bonus.amount : null;
  const initialComment = bonus.comment || "";
  const [amount, setAmount] = useState(initialAmount);
  const [comment, setComment] = useState(initialComment);
  const [disabled, setDisabled] = useState(true);
  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        <InputNumber
          style={{
            backgroundColor: "#f0f0f0",
            borderRadius: "11px",
            borderColor: "#f0f0f0",
            width: "100px",
            marginRight: 5
          }}
          min={0}
          type="number"
          value={amount}
          disabled={isValidated || disabled}
          onChange={setAmount}
        />
        {isExtra && (
          <Input
            style={{
              backgroundColor: "#f0f0f0",
              borderRadius: "11px",
              borderColor: "#f0f0f0",
              width: "300px"
            }}
            disabled={isValidated || disabled}
            value={comment}
            placeholder={amount > 0 ? "commentaire obligatoire" : ""}
            onChange={item => {
              setComment(item.target.value);
            }}
          />
        )}
        {isValidated && <Icon type="lock" />}
        {!isValidated && !hideEdit && disabled && (
          <div
            onClick={() => {
              setDisabled(false);
              action();
            }}
            className="cursor-pointer"
          >
            <Icon type="edit" />
          </div>
        )}
        {(amount !== initialAmount || comment !== initialComment) && (
          <div
            onClick={() => {
              bonus._id ? onUpdate({ ...bonus, amount, comment }) : onCreate(amount, comment);
              setDisabled(true);
            }}
            className="cursor-pointer bg-green-400 text-white mx-2 px-2 py-2"
          >
            {/* <Icon type="save" /> */}
            Enregistrer
          </div>
        )}
        {isExtra && roleId === roles.ADMIN && bonus._id && !isValidated && (
          <div
            onClick={() => {
              onValidate(bonus);
              setDisabled(true);
            }}
            className="cursor-pointer bg-green-400 text-white mx-2 px-2 py-2"
          >
            valider
          </div>
        )}
        {isExtra && roleId === roles.ADMIN && bonus._id && !isValidated && (
          <div
            onClick={() => {
              onReject(bonus);
              setDisabled(true);
            }}
            className="cursor-pointer bg-red-400 text-white mx-2 px-2 py-2"
          >
            rejeter
          </div>
        )}
      </div>
      {bonus.created_at && (
        <div className="my-2 mx-2 text-gray-500">{`renseignée le ${moment(bonus.created_at).format(
          "DD-MM-YYYY"
        )}`}</div>
      )}
    </div>
  );
};
const ValidationButtonOfBonuses = ({ bonuses, token, fetchBonuses }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const bonusesIds = bonuses.filter(bonus => !bonus.validated).map(bonus => bonus._id);

  return (
    <span className="mx-5">
      <Modal
        title="Validation? "
        visible={isVisible}
        onOk={async () => {
          setIsLoading(true);
          try {
            await validateBonuses(bonusesIds, token);
            fetchBonuses();
          } catch (e) {
            handleError(e);
          }
          setIsLoading(false);
          setIsVisible(false);
        }}
        onCancel={() => setIsVisible(false)}
        confirmLoading={isLoading}
      >
        <p>Valider et figer l'ensemble des primes exceptionnelles du dossier</p>
      </Modal>
      <Button
        onClick={() => {
          setIsVisible(true);
        }}
        type="primary"
      >
        Valider les primes exceptionnelles du dossier
      </Button>
    </span>
  );
};
const BonusesTable = ({ fetchBonuses, bonuses = [], clientId, clientLabel, token, roleId }) => {
  const [rangeDate, setRangeDate] = useState([START_DATE_INIT, END_DATE_INIT]);
  const [users, setUsers] = useState([]);
  const [areInactivesVisibles, setAreInactivesVisible] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [selectedUser, setSelectedUser] = useState();
  const displayedUsers = areInactivesVisibles ? users : users.filter(u => u.effectif);

  const isSelectedDayBeforeAllowedModification = moment(rangeDate[0]).isBefore(moment().subtract(1, "month"), "month");

  const filters = {
    match: {
      $and: [
        { $or: [{ "status.id": 2 }, { "status.id": 1 }] },
        {
          $or: [
            {
              $and: [
                { start_date: { $gte: { $date: rangeDate[0] }, $lte: { $date: rangeDate[1] } } },
                { frequency_id: FREQUENCY_ID.MONTHLY }
              ]
            },
            {
              $and: [
                {
                  start_date: {
                    $gte: { $date: moment(rangeDate[0]).startOf("quarter") },
                    $lte: { $date: moment(rangeDate[1]).endOf("quarter") }
                  }
                },
                { frequency_id: FREQUENCY_ID.QUARTERLY }
              ]
            }
          ]
        }
      ],

      "clients.id": clientId
    }
  };

  const resetErrorText = () => {
    setSelectedUser();
    setErrorText(null);
  };
  const initUsers = async () => {
    try {
      const response = await fetchUsers(
        {
          match: { "clients.id": clientId, role_id: { $gt: roleId, $ne: roles.CLIENT } },
          sort: { email: 1 },
          project: { email: 1, effectif: 1 }
        },
        token
      );
      if (Array.isArray(response)) {
        setUsers(response);
      }
    } catch (e) {
      console.log(e);
    }
  };
  const saveBonus = async bonus => {
    resetErrorText();
    try {
      isBonusFormValid(bonus);
      await createBonus(bonus, token);

      fetchBonuses(filters);
    } catch (e) {
      const field = get(["response", "data", "data", "fields", 0], e);

      if (bonus && bonus.user_email) setSelectedUser(bonus.user_email);
      if (field) {
        const message = get(
          ["response", "data", "data", "e", field, "message"],
          e,
          "la requète n'a pu aboutir, veuillez reassayer"
        );
        setErrorText(message);
      } else if (e.response && e.response.data && typeof e.response.data.data === "string")
        setErrorText(e.response.data.data);
      else {
        setErrorText(e.message);
      }
    }
  };

  useEffect(() => {
    initUsers();
  }, [clientId]);

  useEffect(() => {
    fetchBonuses(filters);
  }, [rangeDate, clientId]);

  const columns = [
    {
      title: "Utilisateur",
      dataIndex: "email",
      key: "email",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: user_email => (
        <div>
          <span>{user_email}</span>
          {selectedUser === user_email && <div className="font-bold text-red-400 mb-10">{errorText}</div>}
        </div>
      )
    },

    {
      title: "mensuelle",
      dataIndex: "_id",
      key: "monthly",
      render: (user_id, user) => {
        const bonus = bonuses.find(
          b => b.user_id === user_id && b.type_id === BONUS_TYPE_ID.REGULAR && b.frequency_id === FREQUENCY_ID.MONTHLY
        );
        return (
          <BonusInput
            key={user_id + JSON.stringify(bonus) + rangeDate.toString()}
            bonus={bonus}
            hideEdit={isSelectedDayBeforeAllowedModification}
            onUpdate={saveBonus}
            action={resetErrorText}
            onCreate={amount =>
              saveBonus({
                amount,
                type_id: BONUS_TYPE_ID.REGULAR,
                frequency_id: FREQUENCY_ID.MONTHLY,
                comment: "Prime normale mensuelle",
                user_email: user.email,
                start_date: rangeDate[0]
              })
            }
          />
        );
      }
    },
    {
      title: "trimestrielle",
      dataIndex: "_id",
      key: "quarterly",
      render: (user_id, user) => {
        const bonus = bonuses.find(
          b => b.user_id === user_id && b.type_id === BONUS_TYPE_ID.REGULAR && b.frequency_id === FREQUENCY_ID.QUARTERLY
        );
        return (
          <BonusInput
            key={user_id + JSON.stringify(bonus) + rangeDate.toString()}
            bonus={bonus}
            hideEdit={isSelectedDayBeforeAllowedModification}
            onUpdate={saveBonus}
            action={resetErrorText}
            onCreate={amount =>
              saveBonus({
                amount,
                type_id: BONUS_TYPE_ID.REGULAR,
                frequency_id: FREQUENCY_ID.QUARTERLY,
                comment: "Prime normale trimestrielle",
                user_email: user.email,
                start_date: rangeDate[0]
              })
            }
          />
        );
      }
    },
    {
      title: "exceptionnelle",
      dataIndex: "_id",
      key: "extra",
      render: (user_id, user) => {
        const bonus = bonuses.find(b => b.user_id === user_id && b.type_id === BONUS_TYPE_ID.EXTRA);
        return (
          <BonusInput
            key={user_id + JSON.stringify(bonus) + rangeDate.toString()}
            bonus={bonus}
            hideEdit={isSelectedDayBeforeAllowedModification}
            onUpdate={saveBonus}
            isExtra
            action={resetErrorText}
            onCreate={(amount, comment) =>
              saveBonus({
                amount,
                type_id: BONUS_TYPE_ID.EXTRA,
                frequency_id: FREQUENCY_ID.MONTHLY,
                comment,
                user_email: user.email,
                start_date: rangeDate[0]
              })
            }
            onValidate={async bonus => {
              try {
                await validateBonus(bonus, token);
                fetchBonuses(filters);
              } catch (e) {
                handleError(e);
              }
            }}
            onReject={async bonus => {
              try {
                await rejectBonus(bonus, token);
                fetchBonuses(filters);
              } catch (e) {
                handleError(e);
              }
            }}
            roleId={roleId}
          />
        );
      }
    }
  ];
  return (
    <Container>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <div style={{ fontSize: 30, width: "20%" }}>{`Primes ${clientLabel}`}</div>
        <div className="flex flex-col justify-center items-center" style={{ width: "40%" }}>
          <span>Primes mensuelles/exceptionnelles à renseigner en </span>
          <span style={{ fontSize: 25, margin: 10 }}>
            {moment(rangeDate[0])
              .add(1, "month")
              .format("MMMM")}
          </span>
          <span>basées sur l'activité du mois de</span>
          <SelectMonthBonus setRangeDate={setRangeDate} rangeDate={rangeDate} />
          <span>Primes trimestrielles basées sur l'activté du trimestre : </span>
          <span style={{ fontSize: 20, marginTop: 10 }}>{`${moment(rangeDate[0])
            .startOf("quarter")
            .format("MMMM")} - ${moment(rangeDate[0])
            .endOf("quarter")
            .format("MMMM")}`}</span>
        </div>
        <div style={{ width: "30%" }}>
          <span style={{ fontWeight: "bold" }}>{`Information importante : `}</span>
          <span>
            {`les primes à renseigner en ce mois de ${moment().format(
              "MMMM"
            )} sont basées sur l'activité du mois précédent (${moment()
              .subtract(1, "month")
              .format("MMMM")}). `}
          </span>
          {moment().format("MMMM") === moment(rangeDate[0]).format("MMMM") && (
            <div style={{ fontWeight: "bold", color: "red" }}>
              {`ATTENTION : Une prime pour le mois de ${moment().format(
                "MMMM"
              )} ne doit être renseignée que dans le cas d'un départ de la société durant ce mois de ${moment().format(
                "MMMM YYYY"
              )}.`}
            </div>
          )}
        </div>
      </div>

      <div>
        <Button onClick={() => setAreInactivesVisible(v => !v)}>
          {areInactivesVisibles ? "cacher les inactifs" : "afficher les inactifs"}
        </Button>
        <ValidationButtonOfBonuses
          token={token}
          bonuses={bonuses.filter(b => b.type_id === BONUS_TYPE_ID.EXTRA)}
          fetchBonuses={() => fetchBonuses(filters)}
        />
      </div>
      {errorText && !selectedUser && <div className="font-bold text-red-400 mb-10">{errorText}</div>}
      <Table rowKey="_id" columns={columns} dataSource={displayedUsers} pagination={false} align="center" />
    </Container>
  );
};

export default BonusesTable;



// WEBPACK FOOTER //
// src/containers/admin/premium/BonusesTable.js